import cachedHttpClient from '../cached-http-client';
import { ApiResponse } from '../../types/responses/api-response';
import { EventAnalyticsStatusFilter, ExportFormat, DateRangePeriod } from '../../types/analytics';
import { EventStatus } from '../../types/events-unified';

export interface EventAnalytics {
  id: string;
  title: string;
  type: string;
  /**
   * AD-360: сервер кладёт сюда каноничный статус сущности
   * (BE `analytics/services/domain/event-analytics.service.ts`: `status: event.status`),
   * а не слово из языка фильтра. Прежнее объявление `EventAnalyticsStatus`
   * описывало форму, которой в ответе не бывает.
   */
  status: EventStatus;
  startDate: string;
  endDate: string;
  participantsCount: number;
  registrationsCount: number;
  conversionRate: number;
  revenue: number;
  averageRating: number;
  completionRate: number;
  // Дополнительные поля для аналитики
  organizerId: string;
  organizerName: string;
  category: string;
  location: string;
  capacity: number;
  attendanceRate: number;
  engagementScore: number;
  totalInteractions: number;
  socialShares: number;
  feedbackCount: number;
  averageSessionDuration: number;
  peakAttendance: number;
  revenuePerParticipant: number;
  costPerAcquisition: number;
  returnOnInvestment: number;
}

export interface EventMetrics {
  overview: {
    totalEvents: number;
    activeEvents: number;
    completedEvents: number;
    cancelledEvents: number;
    upcomingEvents: number;
  };
  participation: {
    totalParticipants: number;
    totalRegistrations: number;
    averageConversionRate: number;
    averageAttendanceRate: number;
    peakAttendance: number;
  };
  financial: {
    totalRevenue: number;
    averageRevenuePerEvent: number;
    averageRevenuePerParticipant: number;
    costPerAcquisition: number;
    returnOnInvestment: number;
  };
  engagement: {
    averageRating: number;
    averageEngagementScore: number;
    totalInteractions: number;
    socialShares: number;
    feedbackCount: number;
  };
  performance: {
    averageCompletionRate: number;
    averageSessionDuration: number;
    mostPopularCategory: string;
    mostActiveTime: string;
    mostActiveDay: string;
  };
  trends: {
    eventsByMonth: Array<{ month: string; count: number; revenue: number }>;
    participantsByEvent: Array<{ eventId: string; eventTitle: string; participants: number }>;
    revenueByEvent: Array<{ eventId: string; eventTitle: string; revenue: number }>;
    popularCategories: Array<{ category: string; count: number; revenue: number }>;
  };
}

export interface EventsFilters {
  eventId?: string;
  dateRange?: DateRangePeriod;
  eventType?: string;
  status?: EventAnalyticsStatusFilter;
  category?: string;
  organizerId?: string;
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
}

export const eventsAnalyticsApi = {
  // Получение списка событий с аналитикой
  getEvents: async (filters: EventsFilters = {}): Promise<ApiResponse<{
    events: EventAnalytics[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    filters: {
      dateRange: string;
      eventType: string;
      status: string;
      category: string;
    };
  }>> => {
    return cachedHttpClient.get('/analytics/events', { params: filters });
  },

  // Получение метрик событий
  getEventsMetrics: async (filters: EventsFilters = {}): Promise<ApiResponse<EventMetrics>> => {
    return cachedHttpClient.get('/analytics/events/metrics', { params: filters });
  },

  // Получение аналитики конкретного события
  getEventAnalytics: async (eventId: string, filters: EventsFilters = {}): Promise<ApiResponse<{
    event: EventAnalytics;
    participants: Array<{
      id: string;
      name: string;
      email: string;
      registrationDate: string;
      attendanceStatus: string;
      engagementScore: number;
      feedback: string;
    }>;
    metrics: {
      attendanceRate: number;
      engagementScore: number;
      revenue: number;
      feedback: {
        averageRating: number;
        totalResponses: number;
        positiveResponses: number;
        negativeResponses: number;
      };
    };
  }>> => {
    return cachedHttpClient.get(`/analytics/events/${eventId}`, { params: filters });
  },

  // Получение воронки конверсии
  getConversionFunnel: async (filters: EventsFilters = {}): Promise<ApiResponse<{
    funnel: Array<{
      stage: string;
      count: number;
      percentage: number;
      conversionRate: number;
    }>;
    totalRegistrations: number;
    totalAttendees: number;
    overallConversionRate: number;
  }>> => {
    return cachedHttpClient.get('/analytics/events/conversion-funnel', { params: filters });
  },

  // Получение метрик конверсии
  getConversionsMetrics: async (filters: EventsFilters = {}): Promise<ApiResponse<{
    overview: {
      totalRegistrations: number;
      totalAttendees: number;
      overallConversionRate: number;
      averageTimeToRegister: number;
      averageTimeToAttend: number;
    };
    byEventType: Array<{
      eventType: string;
      registrations: number;
      attendees: number;
      conversionRate: number;
    }>;
    byCategory: Array<{
      category: string;
      registrations: number;
      attendees: number;
      conversionRate: number;
    }>;
    trends: Array<{
      date: string;
      registrations: number;
      attendees: number;
      conversionRate: number;
    }>;
  }>> => {
    return cachedHttpClient.get('/analytics/events/conversions', { params: filters });
  },

  // Экспорт данных событий
  exportEvents: async (format: ExportFormat, filters: EventsFilters = {}): Promise<ApiResponse<{
    downloadUrl: string;
    filename: string;
    expiresAt: string;
    fileSize: number;
  }>> => {
    return cachedHttpClient.get(`/analytics/events/export`, { params: { ...filters, format } });
  }
};

export default eventsAnalyticsApi;
